/* eslint-disable */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { NotificationsNoneOutlined } from '@mui/icons-material'
import { useRecoilRefresher_UNSTABLE, useRecoilValueLoadable } from 'recoil'
import { patch } from '../http/restservice'
import { NotificationsSelector } from '../store/AccountDetailsStore'
import { getParsedNotificationItems } from '../utils/notificationUtils'
import { useThemeMode } from '../Provider/ThemeContextProvider'
import { useNavigate } from 'react-router-dom'

const TopbarNotifications = ({ className = '', variant = 'desktop', onOpenChange }) => {
  const navigate = useNavigate()
  const { state, contents: notifications } = useRecoilValueLoadable(NotificationsSelector)
  const refreshNotifications = useRecoilRefresher_UNSTABLE(NotificationsSelector)
  const { darkMode } = useThemeMode()
  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [notificationItems, setNotificationItems] = useState([])
  const [panelStyle, setPanelStyle] = useState({})
  const rootRef = useRef(null)
  const closeTimeoutRef = useRef(null)

  useEffect(() => {
    if (state === 'hasValue') {
      setNotificationItems(getParsedNotificationItems(notifications, { onlyUnread: true }))
    }

    if (state === 'hasError') {
      setNotificationItems([])
    }
  }, [state, notifications])

  const hasUnreadNotifications = useMemo(
    () => notificationItems.some((notification) => notification.isUnread),
    [notificationItems],
  )
  const hasNotifications = notificationItems.length > 0

  useEffect(() => {
    onOpenChange?.(isOpen)
  }, [isOpen, onOpenChange])

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    },
    [],
  )

  useLayoutEffect(() => {
    if (!isOpen) return undefined

    const syncPanelStyle = () => {
      const topbar = rootRef.current?.closest('.fabits-mobile-topbar, .fabits-desktop-topbar')
      if (!topbar) return

      const topbarRect = topbar.getBoundingClientRect()

      setPanelStyle({
        '--fabits-topbar-notification-top': `${Math.round(topbarRect.bottom)}px`,
      })
    }

    syncPanelStyle()
    window.addEventListener('resize', syncPanelStyle)

    return () => window.removeEventListener('resize', syncPanelStyle)
  }, [darkMode, isOpen, variant])

  const openPanel = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = null
    setIsClosing(false)
    setIsOpen(true)
  }

  const closePanel = () => {
    if (!isOpen || isClosing) return

    setIsClosing(true)
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
      setIsClosing(false)
      closeTimeoutRef.current = null
    }, 210)
  }

  useEffect(() => {
    if (!hasNotifications && isOpen && !isClosing) closePanel()
  }, [hasNotifications, isClosing, isOpen])

  const markNotificationsAsRead = async (notificationIds) => {
    const ids = notificationIds.filter(Boolean)

    if (!ids.length) {
      setNotificationItems([])
      return 0
    }

    const responses = await Promise.all(
      ids.map((notificationId) =>
        patch(`customerservice/api/notification/${notificationId}/markAsRead`),
      ),
    )
    const successfulIds = ids.filter((_, index) => !responses[index]?.isError)

    if (successfulIds.length) {
      setNotificationItems((prevState) =>
        prevState.filter((notification) => !successfulIds.includes(notification.notificationId)),
      )
      refreshNotifications()
    }

    return successfulIds.length
  }

  const handleBellClick = async () => {
    if (isOpen && !isClosing) {
      if (notificationItems.length) {
        await markNotificationsAsRead(
          notificationItems.map((notification) => notification.notificationId),
        )
      }
      closePanel()
      return
    }

    openPanel()
  }

  const handleNotificationClick = async (notification) => {
    const markedCount = await markNotificationsAsRead([notification.notificationId])
    const shouldNavigate = notification.action.toUpperCase() === 'DEEP_LINK' && notification.route

    if (shouldNavigate) navigate(notification.route)
    if (shouldNavigate || (markedCount && notificationItems.length <= 1)) closePanel()
  }

  if (!hasNotifications && !isOpen) return null

  return (
    <div className={`fabits-topbar-notifications ${className}`} ref={rootRef}>
      <button
        type='button'
        className={`fabits-topbar-notification-button ${
          hasUnreadNotifications ? 'is-unread' : 'is-read'
        }`}
        aria-label={hasUnreadNotifications ? 'Unread notifications' : 'Notifications'}
        aria-expanded={isOpen && !isClosing}
        data-testid='topbar-notification-bell'
        onClick={handleBellClick}
      >
        <NotificationsNoneOutlined className='fabits-topbar-notification-icon' />
        {hasUnreadNotifications && <span className='fabits-topbar-notification-dot' />}
      </button>

      {isOpen && (
        <div
          className={`fabits-topbar-notification-panel is-${variant} ${
            isClosing ? 'is-closing' : ''
          }`}
          data-testid='topbar-notification-panel'
          aria-live='polite'
          style={panelStyle}
        >
          <div className='fabits-topbar-notification-panel-inner'>
            <div className='fabits-topbar-notification-panel-header'>Notifications</div>
            {hasNotifications ? (
              <div className='fabits-topbar-notification-list'>
                {notificationItems.map((notification) => (
                  <button
                    type='button'
                    key={notification.id}
                    className='fabits-topbar-notification-item'
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <span className='fabits-topbar-notification-title'>
                      {notification.title || 'Notification'}
                    </span>
                    <span className='fabits-topbar-notification-description'>
                      {notification.description || notification.text}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}

export default TopbarNotifications
